import React, { useEffect, useRef } from "react"

function parseCssColor(value) {
  const text = String(value || "").trim()
  const hex = text.match(/^#([0-9a-f]{6})$/i)
  if (hex) {
    const n = parseInt(hex[1], 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  const rgb = text.match(/(\d+)[,\s]+(\d+)[,\s]+(\d+)/)
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])]
  return [22, 50, 74]
}

function buildStars() {
  const stars = []
  const arms = 2
  const perArm = 200

  for (let arm = 0; arm < arms; arm += 1) {
    const offset = (arm / arms) * Math.PI
    for (let index = 0; index < perArm; index += 1) {
      const along = (index + 0.5) / perArm
      const dist = along ** 0.62
      const theta = offset + dist * Math.PI * 3.5 + (Math.random() - 0.5) * (0.18 + dist * 0.42)
      const scatter = (Math.random() - 0.5) * (0.025 + dist * 0.06)
      const px = Math.cos(theta)
      const py = Math.sin(theta)
      stars.push({
        x: px * dist - py * scatter,
        y: py * dist + px * scatter,
        size: Math.random() < 0.05 ? 1.55 : 0.4 + Math.random() * 0.7,
        alpha: 0.12 + Math.random() * 0.16,
        phase: Math.random() * Math.PI * 2,
      })
    }
  }

  for (let index = 0; index < 90; index += 1) {
    const dist = Math.random() ** 1.7 * 0.26
    const theta = Math.random() * Math.PI * 2
    stars.push({
      x: Math.cos(theta) * dist,
      y: Math.sin(theta) * dist,
      size: 0.35 + Math.random() * 0.75,
      alpha: 0.16 + Math.random() * 0.14,
      phase: Math.random() * Math.PI * 2,
    })
  }

  return stars
}

const STARS = buildStars()
const TILT = 0.78

export default function MilkyWay() {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined
    const ctx = canvas.getContext("2d")
    if (!ctx || typeof ctx.arc !== "function" || typeof ctx.clearRect !== "function") return undefined

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    let frame = 0
    let spin = 0.35

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const rect = canvas.getBoundingClientRect()
      canvas.width = Math.max(1, Math.floor(rect.width * dpr))
      canvas.height = Math.max(1, Math.floor(rect.height * dpr))
      if (typeof ctx.setTransform === "function") ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const paint = () => {
      const width = canvas.clientWidth || canvas.getBoundingClientRect().width
      const height = canvas.clientHeight || canvas.getBoundingClientRect().height
      if (width < 2 || height < 2) return
      ctx.clearRect(0, 0, width, height)

      const radius = Math.min(width, height) * 0.34
      const cx = width / 2
      const cy = height / 2
      const rgb = parseCssColor(getComputedStyle(canvas).color).join(",")

      if (
        typeof ctx.save === "function"
        && typeof ctx.restore === "function"
        && typeof ctx.createRadialGradient === "function"
      ) {
        ctx.save()
        ctx.translate(cx, cy)
        ctx.scale(1, TILT)
        const glow = ctx.createRadialGradient(0, 0, radius * 0.04, 0, 0, radius)
        glow.addColorStop(0, `rgba(${rgb}, 0.09)`)
        glow.addColorStop(0.32, `rgba(${rgb}, 0.035)`)
        glow.addColorStop(1, `rgba(${rgb}, 0)`)
        ctx.fillStyle = glow
        ctx.beginPath()
        ctx.arc(0, 0, radius, 0, Math.PI * 2)
        ctx.fill()
        ctx.restore()
      }

      const cos = Math.cos(spin)
      const sin = Math.sin(spin)
      STARS.forEach((star) => {
        const rx = star.x * cos - star.y * sin
        const ry = (star.x * sin + star.y * cos) * TILT
        const twinkle = reduce ? 1 : 0.9 + Math.sin(spin * 1.6 + star.phase) * 0.1
        ctx.beginPath()
        ctx.arc(
          cx + rx * radius,
          cy + ry * radius,
          Math.max(0.45, star.size * (radius / 160)),
          0,
          Math.PI * 2,
        )
        ctx.fillStyle = `rgba(${rgb}, ${(star.alpha * twinkle).toFixed(3)})`
        ctx.fill()
      })

      if (!reduce) spin += 0.0011
    }

    resize()
    window.addEventListener("resize", resize)
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(resize) : null
    observer?.observe(canvas)
    paint()
    if (!reduce) {
      const loop = () => {
        paint()
        frame = window.requestAnimationFrame(loop)
      }
      frame = window.requestAnimationFrame(loop)
    }

    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener("resize", resize)
      observer?.disconnect()
    }
  }, [])

  return <canvas ref={canvasRef} className="home-globe" aria-hidden />
}
